import React from 'react';
import { styled } from '@compiled/react';
// eslint-disable-next-line jira/graphql/restrict-graphql-in-unnamed-files
import { graphql, useFragment } from 'react-relay';
import Button from '@atlaskit/button';
import Heading from '@atlaskit/heading';
import { token } from '@atlaskit/tokens';
import { useIntl } from '@atlassian/jira-intl';
import type { emptyState_softwareReleasesVersionRelatedWork_RelatedWorkEmptyState_jiraVersion$key } from '@atlassian/jira-relay/src/__generated__/emptyState_softwareReleasesVersionRelatedWork_RelatedWorkEmptyState_jiraVersion.graphql';
import type { emptyState_softwareReleasesVersionRelatedWork_RelatedWorkEmptyState_relatedWorkEdges$key } from '@atlassian/jira-relay/src/__generated__/emptyState_softwareReleasesVersionRelatedWork_RelatedWorkEmptyState_relatedWorkEdges.graphql';
import { useAddRelatedWorkExperienceFail } from '@atlassian/jira-software-releases-add-related-work-to-version/src/services/experience-tracker/add-related-work/index.tsx';
import { AddRelatedWorkPopup } from '@atlassian/jira-software-releases-add-related-work-to-version/src/ui/add-related-work-popup/async.tsx';
import { ErrorBoundary } from '../../common/utils/error-boundary.tsx';
import { VersionRelatedWorkExperienceSuccessTracker } from '../../services/related-work-experience-tracker/VersionRelatedWorkExperienceSuccessTracker.tsx';
import documentsIllustrationUrl from './assets/documents-illustration.svg';
import messages from './messages.tsx';

type Props = {
	jiraVersion: emptyState_softwareReleasesVersionRelatedWork_RelatedWorkEmptyState_jiraVersion$key;
	relatedWorkEdges: emptyState_softwareReleasesVersionRelatedWork_RelatedWorkEmptyState_relatedWorkEdges$key;
};

export const RelatedWorkEmptyState = ({
	jiraVersion,
	relatedWorkEdges,
}: Props): React.JSX.Element => {
	const { formatMessage } = useIntl();
	const addRelatedWorkExpFailed = useAddRelatedWorkExperienceFail();

	const version = useFragment(
		// eslint-disable-next-line jira/graphql/restrict-graphql-in-unnamed-files
		graphql`
			fragment emptyState_softwareReleasesVersionRelatedWork_RelatedWorkEmptyState_jiraVersion on JiraVersion {
				...async_softwareReleasesAddRelatedWorkToVersion_AddRelatedWorkPopup_jiraVersion
				status
			}
		`,
		jiraVersion,
	);

	const edges = useFragment(
		// eslint-disable-next-line jira/graphql/restrict-graphql-in-unnamed-files
		graphql`
			fragment emptyState_softwareReleasesVersionRelatedWork_RelatedWorkEmptyState_relatedWorkEdges on JiraVersionRelatedWorkV2Edge
			@relay(plural: true) {
				...async_softwareReleasesAddRelatedWorkToVersion_AddRelatedWorkPopup_relatedWorkEdges
			}
		`,
		relatedWorkEdges,
	);

	return (
		<Container>
			<VersionRelatedWorkExperienceSuccessTracker />
			<img src={documentsIllustrationUrl} width={80} height={71.45} alt="" />
			<section>
				<Heading as="h3" size="xsmall">
					{formatMessage(messages.emptyStateHeading)}
				</Heading>
				<Description>{formatMessage(messages.emptyStateDescription)}</Description>
			</section>
			<ErrorBoundary
				id="addRelatedPopupInEmptyState"
				fallback="unmount"
				onError={(location: string, error: Error) => {
					addRelatedWorkExpFailed(location, error);
				}}
			>
				{version.status != null && version.status !== 'ARCHIVED' && (
					<AddRelatedWorkPopup
						jiraVersion={version}
						relatedWorkEdges={edges}
						renderTrigger={({ triggerProps, onClick, isLoading, onMouseOver }) => (
							<Button
								{...triggerProps}
								onClick={onClick}
								testId={
									isLoading
										? undefined
										: 'software-releases-version-related-work.ui.empty-state.button'
								}
								onMouseOver={onMouseOver}
							>
								{formatMessage(messages.add)}
							</Button>
						)}
					/>
				)}
			</ErrorBoundary>
		</Container>
	);
};

// RLSS-2812 color.border.bold might look better than color.border. Personally I feel that color.border is too subtle in Dark mode

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const Container = styled.div({
	display: 'flex',
	alignItems: 'center',
	columnGap: token('space.300'),
	marginTop: token('space.200'),
	paddingTop: token('space.300'),
	paddingRight: token('space.300'),
	paddingBottom: token('space.300'),
	paddingLeft: token('space.300'),
	borderRadius: token('radius.large'),
	borderWidth: token('border.width.selected'),
	borderStyle: 'dashed',

	borderColor: `${token('color.border')}`,
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const Description = styled.p({
	color: token('color.text.subtlest'),
	maxWidth: '548px',
});
